import { MultiDirectedGraph } from 'graphology';
import { Edge as ReactFlowEdge } from 'reactflow';

import { Edge, EdgeType, Edge as PlyEdge, ServiceSlugs, WorkflowNode } from '@ply-io/ply-front-shared-utilities';

import { LocalEdgeTypes, EdgeType, ServerEdgeTypes } from '@/components/FlowTree/flow-tree-service.const';
import { CreateNodeShortcutCommand } from '@/react_app/components';
import { EdgeData } from '@/react_app/components/ReactFlow/types';
import { nodesInRoute, GraphsObject } from '@/utils/workflow/workflow-graph-service';

const doesPathIncludesEndRunNode = (nodes: [WorkflowNode]) => nodes.some(isNodeEndRun);
const isNodeEndRun = (node: WorkflowNode) => node.service_slug === ServiceSlugs.EndRun;
const isServerEdge = (edgeType: EdgeType): edgeType is ServerEdgeTypes =>
	Object.values(ServerEdgeTypes).includes(edgeType as ServerEdgeTypes);
const isNodeReachable = (pathsToNode: [[WorkflowNode]]) => pathsToNode.some((path) => !doesPathIncludesEndRunNode(path));

export const buildReactFlowEdge = (
	edge: PlyEdge,
	edgeType: EdgeType,
	isEdgeAllowedToEdit: boolean,
	onPlusClick: (edge: ReactFlowEdge, shortcut?: CreateNodeShortcutCommand) => void,
	isMoveNodeActive: boolean,
	isEndRunNodePossible: boolean
): ReactFlowEdge<EdgeData> => ({
	...edge,
	type: edgeType,
	id: `${edge.id}`,
	source: `${edge.source}`,
	target: `${edge.target}`,
	sourceHandle: `${edge.id}`,
	data: {
		onClick: onPlusClick,
		branchPath: edge.branch_path,
		isAllowedToEdit: isEdgeAllowedToEdit,
		isMoveNodeActive,
		isEndRunNodePossible,
	},
	focusable: false,
	animated: edgeType === LocalEdgeTypes.AnimatedHelpEdge,
	style: {
		opacity: 0,
	},
});

type NodeId = string;

export const replacePathWithNode = (
	dag: MultiDirectedGraph, // nodes and edges are of same types served to ReactFlow
	nodeA: NodeId,
	nodeB: NodeId,
	replacedNode: { id: string; type: string },
	isEdgeAllowedToEdit: boolean
) => {
	const [nodeIdBeforeNodeA] = dag.inNeighbors(nodeA);
	const [nodeAInEdgeId] = dag.inEdges(nodeA);
	const [nodeIdAfterNodeB] = dag.outNeighbors(nodeB);
	const [nodeBOutEdgeId] = dag.outEdges(nodeB);

	const path = nodesInRoute({ dag } as GraphsObject, nodeA, nodeB);

	const nodeAInEdgeAttributes = dag.getEdgeAttributes(nodeAInEdgeId);
	const nodeBOutEdgeAttributes = dag.getEdgeAttributes(nodeBOutEdgeId);

	/* remove all nodes and edges in path */
	path.forEach((nodeId) => dag.dropNode(nodeId));

	dag.addNode(replacedNode.id, { nodeObject: replacedNode });
	const replacedNodeInEdge = {
		id: nodeAInEdgeAttributes.edgeObject.id,
		source: nodeIdBeforeNodeA,
		target: replacedNode.id,
		type: EdgeType.Next,
	};
	const replacedNodeOutEdge = {
		id: nodeBOutEdgeAttributes.edgeObject.id,
		source: replacedNode.id,
		target: nodeIdAfterNodeB,
		type: EdgeType.Next,
	};
	dag.addEdge(nodeIdBeforeNodeA, replacedNode.id, {
		edgeObject: buildReactFlowEdge(
			replacedNodeInEdge as unknown as Edge,
			EdgeType.Next,
			isEdgeAllowedToEdit,
			nodeAInEdgeAttributes.edgeObject.data.onClick,
			false,
			false
		),
	});
	dag.addEdge(replacedNode.id, nodeIdAfterNodeB, {
		edgeObject: buildReactFlowEdge(
			replacedNodeOutEdge as unknown as Edge,
			EdgeType.Next,
			isEdgeAllowedToEdit,
			nodeBOutEdgeAttributes.edgeObject.data.onClick,
			false,
			false
		),
	});
};

const FlowTreeService = {
	isServerEdge,
	isNodeReachable,
	buildReactFlowEdge,
	replacePathWithNode,
};

export default FlowTreeService;
